import { useState } from "react";
import { MODELS, MUTATIONS, useMultiModel, useMutation } from "../../../api";
import type {
  WorkshopId,
  WorkshopParticipant,
  WorkshopStepInfo,
} from "../../../api/workshop";
import { canManageWorkshop, Roles } from "../../../lib/Roles";
import NotAuthorizedPage from "../../auth/UnauthorizedView/NotAuthorizedPage.jsx";
import { useDragAutoScroll } from "../../../hooks/useDragAutoScroll.jsx";
import Button from "../../../design-system/primitives/components/Button/Button";
import Text from "../../../design-system/primitives/components/Text/Text";
import InvitePanel from "./InvitePanel";
import InviteStudentsButton from "./InviteStudentsButton";
import GroupBoard from "./GroupBoard";
import StudentRow from "./StudentRow";
import {
  NO_SMALL_TEAM_ID,
  useStudentDrop,
  type Group,
  type MoveStudent,
} from "./groups";
import styles from "./StudentsOverview.css";

/**
 * Moving a student between groups is two mutations, not one: add them to
 * the new team (if any), and drop them from whichever team they were in
 * before (if any and if different). Both fire independently — a failure in
 * one shouldn't block the other.
 */
const useMoveStudent = (
  workshopId: WorkshopId,
  students: ReadonlyArray<WorkshopParticipant>,
): MoveStudent => {
  const [doAddUserToSmallTeam] = useMutation(MUTATIONS.addUserToSmallTeam);
  const [doDeleteUserFromSmallTeam] = useMutation(
    MUTATIONS.deleteUserFromSmallTeam,
  );

  return (studentId, newGroupId) => {
    const student = students.find((s) => s.id === studentId);
    if (!student) return;

    const oldGroupId = student.smallTeamId;

    if (newGroupId !== NO_SMALL_TEAM_ID) {
      doAddUserToSmallTeam({
        workshopId,
        userId: studentId,
        smallTeamId: newGroupId,
      })?.catch((error: unknown) => {
        console.error("Adding student to group failed", error);
      });
    }

    if (oldGroupId !== NO_SMALL_TEAM_ID && oldGroupId !== newGroupId) {
      doDeleteUserFromSmallTeam({
        workshopId,
        userId: studentId,
        smallTeamId: oldGroupId,
      })?.catch((error: unknown) => {
        console.error("Removing student from group failed", error);
      });
    }
  };
};

type UnassignedStudentsProps = {
  workshop: { id: WorkshopId };
  /** All invited students — filtered to the unassigned ones internally. */
  students: ReadonlyArray<WorkshopParticipant>;
  groups: ReadonlyArray<Group>;
  moveStudent: MoveStudent;
};

/**
 * The "1. Schüler*innen einladen" panel's real content: students not yet in
 * a group, each draggable into one, or shown the empty-state message once
 * none are left to invite.
 */
const UnassignedStudents = ({
  workshop,
  students,
  groups,
  moveStudent,
}: UnassignedStudentsProps) => {
  const { isOver, drop } = useStudentDrop({
    targetGroupId: NO_SMALL_TEAM_ID,
    moveStudent,
  });

  const unassigned = students.filter(
    (student) => student.smallTeamId === NO_SMALL_TEAM_ID,
  );

  if (unassigned.length === 0) {
    return (
      <div
        ref={drop}
        className={styles.unassignedList({ isDropTarget: isOver })}
      >
        <div className={styles.emptyState}>
          <Text variant="body" color="neutral-100">
            Noch keine Schüler*innen eingeladen
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div ref={drop} className={styles.unassignedList({ isDropTarget: isOver })}>
      {unassigned.map((student) => (
        <StudentRow
          key={student.id}
          student={student}
          workshop={workshop}
          surface="base"
          groups={groups}
          onMoveToGroup={(groupId) => moveStudent(student.id, groupId)}
        />
      ))}
    </div>
  );
};

type AddGroupButtonProps = {
  workshopId: WorkshopId;
  /** Auto-generated name for the new group, e.g. `Gruppe ${count + 1}` — the caller counts the existing groups. */
  name: string;
};

/**
 * "+ Gruppe erstellen" — creates a small team with an auto-generated name;
 * renaming happens afterward via the group card's menu.
 */
const AddGroupButton = ({ workshopId, name }: AddGroupButtonProps) => {
  const [isCreating, setIsCreating] = useState(false);
  const [doCreateSmallTeam] = useMutation(MUTATIONS.createSmallTeam);

  const handleClick = async () => {
    setIsCreating(true);
    try {
      await doCreateSmallTeam({ workshopId, name });
    } catch (error) {
      console.error("Creating a small team failed", error);
    }
    setIsCreating(false);
  };

  return (
    <Button
      title={isCreating ? "Wird erstellt …" : "Gruppe erstellen"}
      iconLeft={isCreating ? undefined : "plus"}
      variant="secondary"
      size="large"
      disabled={isCreating}
      onClick={() => void handleClick()}
    />
  );
};

/**
 * The students tab's real content — this is what InviteOverview's
 * "01 Leer" placeholder screen becomes once actual data exists.
 */
const StudentsOverview = ({ workshop }: { workshop: WorkshopStepInfo }) => {
  const [userInfo, smallTeams] = useMultiModel([
    MODELS.WORKSHOP_USER_INFO(workshop.id),
    MODELS.WORKSHOP_SMALL_TEAMS(workshop.id),
  ]);

  const students = (userInfo?.participants ?? []).filter(
    (participant) => participant.role === Roles.STUDENT,
  );

  const moveStudent = useMoveStudent(workshop.id, students);

  useDragAutoScroll();

  if (!canManageWorkshop(workshop.roles)) {
    return <NotAuthorizedPage />;
  }

  return (
    <div className={styles.columns}>
      <div className={styles.inviteColumn}>
        <InvitePanel
          title="Schüler*innen einladen"
          description="Lade zuerst deine Schüler*innen hier ein"
          action={<InviteStudentsButton workshopId={workshop.id} />}
          actionPosition="header"
          background="stars"
        >
          <UnassignedStudents
            workshop={workshop}
            students={students}
            groups={smallTeams.map((team) => ({
              id: team.id,
              name: team.name,
            }))}
            moveStudent={moveStudent}
          />
        </InvitePanel>
      </div>
      <div className={styles.groupColumn}>
        <InvitePanel
          title="Gruppen einteilen"
          description="Erstelle Gruppen für deine Schüler*innen."
          action={
            <AddGroupButton
              workshopId={workshop.id}
              name={`Gruppe ${smallTeams.length + 1}`}
            />
          }
          actionPosition="footer"
        >
          <GroupBoard
            workshop={workshop}
            smallTeams={smallTeams}
            students={students}
            moveStudent={moveStudent}
          />
        </InvitePanel>
      </div>
    </div>
  );
};

export default StudentsOverview;
